import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useClient } from 'urql'
import {
  GET_COMPANY,
  UPDATE_COMPANY,
  UPDATE_COMPANY_STATUS,
  UPDATE_COMPANY_HISTORY_COMMENT,
  DELETE_COMPANY_HISTORY_COMMENT,
  UPDATE_COMPANY_PERMISSIONS,
  GET_COMPANY_ACCOUNTING_CONTACTS,
  EXCLUDE_CONTACT_FROM_NOTIFICATIONS,
  RESTORE_CONTACT_NOTIFICATIONS,
  NEXT_COMPANY_CORRELATIVE,
} from '../../graphql/companies'
import { resolveModuleHasAccess } from '../../utils/resolveModuleHasAccess'
import {
  isRepositorioParentModuleId,
  withoutRepositorioParentPermission,
} from '../../constants/repositorioParentModule'

// Componente para editar comentarios de historial
function HistoryComment({ comment, commentAuthorEmail, currentUserEmail, onSave, onDelete }) {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(comment || '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Verificar si el usuario actual puede editar/eliminar (es el autor o no hay autor aún)
  const canEdit = !commentAuthorEmail || commentAuthorEmail === currentUserEmail
  const canDelete = comment && commentAuthorEmail === currentUserEmail

  const handleSave = async () => {
    setSaving(true)
    await onSave(value)
    setSaving(false)
    setIsEditing(false)
  }

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      // Auto-cancelar después de 3 segundos
      setTimeout(() => setConfirmDelete(false), 3000)
      return
    }
    setDeleting(true)
    setConfirmDelete(false)
    onDelete().finally(() => setDeleting(false))
  }

  const handleCancel = () => {
    setValue(comment || '')
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <div className="history-comment editing">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Agregar comentario..."
          className="comment-input"
          autoFocus
        />
        <div className="comment-actions">
          <button onClick={handleCancel} disabled={saving} className="comment-btn cancel">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="comment-btn save">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="history-comment">
      {comment ? (
        <div className="comment-content">
          <span className="comment-text">{comment}</span>
          {commentAuthorEmail && (
            <span className="comment-author">— {commentAuthorEmail}</span>
          )}
        </div>
      ) : (
        <span className="comment-placeholder">Sin comentario</span>
      )}
      <div className="comment-buttons">
        {canEdit && (
          <button 
            onClick={() => setIsEditing(true)} 
            className="comment-edit-btn"
            title="Editar comentario"
          >
            Editar
          </button>
        )}
        {canDelete && (
          <button 
            onClick={handleDelete} 
            className="comment-delete-btn"
            title={confirmDelete ? "Haz click de nuevo para confirmar" : "Eliminar comentario"}
            disabled={deleting}
          >
            {deleting ? '...' : confirmDelete ? '¿Confirmar?' : 'Eliminar'}
          </button>
        )}
      </div>
    </div>
  )
}

// Status de empresa disponibles
const ADMIN_STATUS_OPTIONS = [
  { value: 'NORMAL', label: 'Normal', description: 'Empresa activa sin problemas', color: '#27ae60' },
  { value: 'ROJO', label: 'Rojo', description: 'Falta de pago - acceso limitado', color: '#e74c3c' },
  { value: 'BLOQUEADO', label: 'Bloqueado', description: 'Deja de ser cliente - sin acceso', color: '#95a5a6' },
]

// Servicios SCFO disponibles (scfo_plan)
const AVAILABLE_SERVICES = [
  { value: 'servicio1', label: 'Servicio 1', description: 'Plan básico' },
  { value: 'servicio2', label: 'Servicio 2', description: 'Plan intermedio' },
  { value: 'servicio3', label: 'Servicio 3', description: 'Plan avanzado' },
  { value: 'anualizado', label: 'Anualizado', description: 'Plan anualizado' },
]

// Estructura jerárquica de módulos (idéntica a UserDetail)
const MODULE_CATEGORIES = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    subModules: []
  },
  {
    id: 'onboarding',
    name: 'Onboarding',
    subModules: [{ id: 'softlanding', name: 'Configuración Inicial' }],
  },
  {
    id: 'reportes',
    name: 'Reportes',
    subModules: [
      { id: 'reportes_ventas', name: 'Reporte de Ventas' },
      { id: 'reportes_compras', name: 'Reporte de Compras' },
      { id: 'reportes_cobranza', name: 'Reporte de Cobranza' },
      { id: 'reportes_proveedores', name: 'Reporte de Proveedores' },
      { id: 'reportes_dj', name: 'Reporte DDJJ' },
      { id: 'reportes_rrhh', name: 'Reporte RRHH' },
      { id: 'reportes_personalizados', name: 'Personalizados' },
    ]
  },
  {
    id: 'finanzas',
    name: 'Finanzas',
    subModules: [
      { id: 'flujo_caja', name: 'Flujo de Caja' },
      { id: 'eerr', name: 'Estado de Resultados de Gestión' },
      { id: 'eoaf', name: 'EOAF' },
      { id: 'creditos', name: 'Préstamos' },
      { id: 'estructuras_maestras', name: 'Estructuras Maestras (configuración)' },
      { id: 'estado_de_resultados_gestion', name: 'EERR por Estructura Maestra' },
      { id: 'balance_clasificado_gestion', name: 'Balance Clasificado por Estructura Maestra' },
    ]
  },
  {
    id: 'documentos',
    name: 'Documentos',
    subModules: [
      { id: 'documentos_tributarios', name: 'DTEs' },
      { id: 'boletas', name: 'Boletas de Honorarios' },
      { id: 'obligaciones', name: 'Obligaciones' },
      { id: 'cuotas_creditos', name: 'Cuotas de Créditos' },
      { id: 'din', name: 'Declaraciones de Ingreso' },
    ]
  },
  {
    id: 'operaciones',
    name: 'Operaciones',
    subModules: [
      { id: 'conciliacion', name: 'Conciliación Bancaria' },
      { id: 'cobranza', name: 'Cobranza' },
      { id: 'pago_proveedores', name: 'Pago a Proveedores' },
      { id: 'cuadre_cartolas', name: 'Cuadre de Cartolas' },
      { id: 'tarjetas_credito', name: 'Tarjetas de Crédito' },
    ]
  },
  {
    id: 'impuestos',
    name: 'Impuestos',
    subModules: [
      { id: 'formulario_29', name: 'Formulario 29' },
      { id: 'consolidado_f29', name: 'Consolidado F29' },
      { id: 'multas_sii', name: 'Multas en el SII' },
      { id: 'renta', name: 'Declaración de Renta' },
      { id: 'estado_dj', name: 'Estado de las Declaraciones Juradas' },
      { id: 'cuadre_formulario_29', name: 'Cuadre Formulario 29' },
    ]
  },
  {
    id: 'contabilidad',
    name: 'Contabilidad',
    subModules: [
      { id: 'balance_8_columnas', name: 'Balance 8 Columnas' },
      { id: 'balance_clasificado', name: 'Balance Clasificado' },
      { id: 'eerr_contable', name: 'Estado de Resultados Contable' },
      { id: 'libro_mayor', name: 'Libro Mayor' },
      { id: 'libro_diario', name: 'Libro Diario' },
      { id: 'asientos', name: 'Asientos Contables' },
      { id: 'plan_cuentas', name: 'Plan de Cuentas' },
      { id: 'centralizacion_contable', name: 'Centralización Contable' },
      { id: 'libro_compra_venta', name: 'Libro Compra/Venta' },
      { id: 'cierre_apertura_contable', name: 'Cierre/Apertura Contable' },
    ]
  },
  {
    id: 'recursos_humanos',
    name: 'Recursos Humanos',
    subModules: [
      { id: 'haberes_variables', name: 'Haberes Variables' },
      { id: 'cargar_nuevo_empleado', name: 'Cargar Nuevo Empleado' },
      { id: 'contratos_proximos_a_vencer', name: 'Contratos Próximos a Vencer' },
      { id: 'validador_liquidaciones', name: 'Validador de Liquidaciones' },
    ]
  },
  {
    id: 'repositorio',
    name: 'Repositorio',
    subModules: [
      { id: 'repositorio_informacion_inicial', name: 'Información Inicial' },
      { id: 'repositorio_informes_financieros', name: 'Informes Contables anuales' },
      { id: 'repositorio_presupuestos', name: 'Presupuestos' },
      { id: 'repositorio_certificados', name: 'Certificados' },
      { id: 'repositorio_formularios', name: 'Formularios' },
      { id: 'repositorio_libro_remuneraciones', name: 'Libros de Remuneraciones' },
      { id: 'repositorio_haberes_variables', name: 'Haberes Variables' },
      { id: 'repositorio_directorio', name: 'Informes Financieros Mensuales' },
      { id: 'repositorio_auditorias', name: 'Auditorías' },
      { id: 'repositorio_cartola_diaria', name: 'Cartola Diaria' },
      { id: 'repositorio_proveedores_hoja', name: 'Hoja Proveedores' },
      { id: 'repositorio_flujo_caja_mensual', name: 'Flujo de Caja Mensual' },
      { id: 'repositorio_flujo_caja_quincenal', name: 'Flujo de Caja Quincenal' },
      { id: 'repositorio_flujo_caja_semanal', name: 'Flujo de Caja Semanal' },
    ]
  },
  {
    id: 'seguimiento',
    name: 'Seguimiento',
    subModules: [
      { id: 'ficha_cliente', name: 'Ficha Cliente' },
      { id: 'avance_contable', name: 'Estado de Avance' },
    ]
  },
  {
    id: 'ajustes',
    name: 'Ajustes',
    subModules: [
      { id: 'conexiones', name: 'Conexiones' },
      { id: 'agenda', name: 'Contactos' },
      { id: 'configuracion', name: 'Configuración' },
      { id: 'descargas', name: 'Descargas' },
    ]
  },
]

// Obtener todos los IDs de módulos (categorías + sub-módulos)
const getAllModuleIds = () => {
  const ids = []
  MODULE_CATEGORIES.forEach(cat => {
    if (!isRepositorioParentModuleId(cat.id)) ids.push(cat.id)
    cat.subModules.forEach(sub => ids.push(sub.id))
  })
  return ids
}

// Modal de Permisos de Módulos para Empresas (folder-tree, idéntico a Usuarios)
function PermissionsModal({ company, isOpen, onClose, onSave }) {
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  useEffect(() => {
    if (isOpen && company) {
      const allModuleIds = getAllModuleIds()
      const initialPermissions = allModuleIds.map(moduleId => {
        const existingPerm = company.modulePermissions?.find(p => p.moduleId === moduleId)
        return {
          moduleId,
          hasAccess: resolveModuleHasAccess(
            existingPerm,
            moduleId,
            true,
            undefined,
            company.modulePermissions,
          ),
          excludedFeatures: existingPerm?.excludedFeatures || [],
        }
      })
      setPermissions(initialPermissions)
    }
  }, [isOpen, company])

  // Toggle categoría principal (afecta todos sus sub-módulos)
  const toggleCategory = (categoryId, subModuleIds) => {
    setPermissions(prev => {
      const categoryPerm = prev.find(p => p.moduleId === categoryId)
      const newAccess = !(categoryPerm?.hasAccess ?? true)
      
      return prev.map(p => {
        if (p.moduleId === categoryId || subModuleIds.includes(p.moduleId)) {
          return { ...p, hasAccess: newAccess }
        }
        return p
      })
    })
  }

  // Toggle sub-módulo individual
  const toggleSubModule = (subModuleId) => {
    setPermissions(prev => 
      prev.map(p => 
        p.moduleId === subModuleId 
          ? { ...p, hasAccess: !p.hasAccess }
          : p
      )
    )
  }

  // Expandir/colapsar categoría
  const toggleExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  // Verificar si la categoría tiene acceso (todos sus sub-módulos tienen acceso)
  const getCategoryAccess = (categoryId, subModuleIds) => {
    const categoryPerm = permissions.find(p => p.moduleId === categoryId)
    if (subModuleIds.length === 0) {
      return categoryPerm?.hasAccess ?? true
    }
    const allSubsHaveAccess = subModuleIds.every(id => {
      const perm = permissions.find(p => p.moduleId === id)
      return perm?.hasAccess ?? true
    })
    return allSubsHaveAccess
  }

  // Contar sub-módulos restringidos
  const countRestrictedSubs = (subModuleIds) => {
    return subModuleIds.filter(id => {
      const perm = permissions.find(p => p.moduleId === id)
      return perm?.hasAccess === false
    }).length
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      await onSave(withoutRepositorioParentPermission(permissions))
      onClose()
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content permissions-modal folder-style" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Permisos de Módulos</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="modal-description">
            Configura el acceso a módulos y funcionalidades específicas para esta empresa.
          </p>
          
          <div className="folder-tree">
            {MODULE_CATEGORIES.map(category => {
              const subModuleIds = category.subModules.map(s => s.id)
              const hasSubModules = subModuleIds.length > 0
              const isExpanded = expandedCategories[category.id]
              const categoryHasAccess = getCategoryAccess(category.id, subModuleIds)
              const restrictedCount = countRestrictedSubs(subModuleIds)
              
              return (
                <div key={category.id} className="folder-item">
                  {/* Carpeta principal */}
                  <div className={`folder-header ${!categoryHasAccess ? 'disabled' : ''}`}>
                    {hasSubModules ? (
                      <button 
                        className="folder-expand-btn"
                        onClick={() => toggleExpanded(category.id)}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    ) : (
                      <span className="folder-expand-placeholder"></span>
                    )}
                    <span className="folder-name" onClick={() => hasSubModules && toggleExpanded(category.id)}>
                      {category.name}
                    </span>
                    {hasSubModules && restrictedCount > 0 && (
                      <span className="restricted-badge">
                        {restrictedCount}/{subModuleIds.length} restringido{restrictedCount > 1 ? 's' : ''}
                      </span>
                    )}
                    {!isRepositorioParentModuleId(category.id) && (
                    <label className="folder-toggle">
                      <input
                        type="checkbox"
                        checked={categoryHasAccess}
                        onChange={() => toggleCategory(category.id, subModuleIds)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    )}
                  </div>
                  
                  {/* Sub-módulos */}
                  {hasSubModules && isExpanded && (
                    <div className="folder-children">
                      {category.subModules.map(subMod => {
                        const subPerm = permissions.find(p => p.moduleId === subMod.id)
                        const subHasAccess = subPerm?.hasAccess ?? true
                        
                        return (
                          <div key={subMod.id} className={`folder-child ${!subHasAccess ? 'disabled' : ''}`}>
                            <span className="child-name">{subMod.name}</span>
                            <label className="folder-toggle">
                              <input
                                type="checkbox"
                                checked={subHasAccess}
                                onChange={() => toggleSubModule(subMod.id)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="action-btn secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button className="action-btn primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ServicesModal({ company, onClose, onConfirm }) {
  const [selectedService, setSelectedService] = useState(company?.scfo_plan || '')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onConfirm(selectedService)
    setSaving(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Editar Servicio - {company?.name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ marginBottom: '1rem', color: '#7f8c8d' }}>
              Selecciona el servicio activo para esta empresa:
            </p>
            <div className="services-list">
              {AVAILABLE_SERVICES.map((service) => (
                <label
                  key={service.value}
                  className={`service-radio ${selectedService === service.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="scfo_service"
                    checked={selectedService === service.value}
                    onChange={() => setSelectedService(service.value)}
                  />
                  <div className="service-info">
                    <span className="service-name">{service.label}</span>
                    <span className="service-desc">{service.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="action-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="action-btn primary" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StatusModal({ company, onClose, onConfirm }) {
  const [selectedStatus, setSelectedStatus] = useState(company?.adminStatus || 'NORMAL')
  // Prellenar fecha si ya existe (formato YYYY-MM-DD para input type="date")
  const initialExitDate = company?.exitDate 
    ? new Date(company.exitDate).toISOString().split('T')[0]
    : ''
  const [exitDate, setExitDate] = useState(initialExitDate)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (selectedStatus === company?.adminStatus) {
      onClose()
      return
    }
    // Validar que se ingrese fecha de salida si es BLOQUEADO
    if (selectedStatus === 'BLOQUEADO' && !exitDate) {
      alert('Debes ingresar la fecha de salida para bloquear la empresa')
      return
    }
    setSaving(true)
    await onConfirm(selectedStatus, exitDate)
    setSaving(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Cambiar Estado - {company?.name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p style={{ marginBottom: '1rem', color: '#7f8c8d' }}>
              Selecciona el nuevo estado para esta empresa:
            </p>
            
            <div className="status-options">
              {ADMIN_STATUS_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`status-option ${selectedStatus === option.value ? 'selected' : ''}`}
                  style={{ borderColor: selectedStatus === option.value ? option.color : undefined }}
                >
                  <input
                    type="radio"
                    name="adminStatus"
                    value={option.value}
                    checked={selectedStatus === option.value}
                    onChange={() => setSelectedStatus(option.value)}
                  />
                  <div className="status-option-info">
                    <span className="status-option-label" style={{ color: option.color }}>
                      ● {option.label}
                    </span>
                    <span className="status-option-desc">{option.description}</span>
                  </div>
                </label>
              ))}
            </div>

            {selectedStatus === 'BLOQUEADO' && (
              <>
                <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.95rem', color: '#2d3748' }}>
                    Fecha de Salida <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={exitDate}
                    onChange={(e) => setExitDate(e.target.value)}
                    className="form-input"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e74c3c',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'all 0.2s',
                      backgroundColor: '#fff',
                    }}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#718096' }}>
                    Ingresa la fecha en que el cliente dejará de usar el servicio
                  </p>
                </div>
                <div className="warning-box" style={{ marginTop: '1rem' }}>
                  <strong>⚠️ Advertencia:</strong> Al marcar como BLOQUEADO, los usuarios de esta empresa 
                  <strong> no podrán acceder al portal</strong>.
                </div>
              </>
            )}

            {selectedStatus === 'ROJO' && (
              <div className="warning-box warning-yellow" style={{ marginTop: '1rem' }}>
                <strong> Nota:</strong> Los usuarios podrán seguir accediendo al portal, pero se les 
                mostrará una advertencia sobre el estado de pago.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="action-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="action-btn primary" 
              disabled={saving || (selectedStatus === 'BLOQUEADO' && !exitDate)}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StatusBadge({ status }) {
  const statusMap = {
    NORMAL: { class: 'normal', label: 'Normal' },
    ROJO: { class: 'rojo', label: 'Rojo' },
    BLOQUEADO: { class: 'bloqueado', label: 'Bloqueado' },
  }

  const statusInfo = statusMap[status] || statusMap.NORMAL

  return (
    <span className={`status-badge ${statusInfo.class}`}>
      <span className="status-dot"></span>
      {statusInfo.label}
    </span>
  )
}

// ============================================================================
// Sección: Notificaciones a Contrapartes Contables (opt-out por email)
// Permite excluir/reactivar emails de los correos automáticos como
// "Factura Rechazada" sin tocar la configuración del cliente en softlanding.
// ============================================================================
function AccountingNotificationsSection({ companyId, currentUserEmail }) {
  const NOTIFICATION_TYPE = 'invoice_rejected'

  const [{ data, fetching, error }, refetch] = useQuery({
    query: GET_COMPANY_ACCOUNTING_CONTACTS,
    variables: { companyId },
    requestPolicy: 'network-only',
  })

  const [, excludeContact] = useMutation(EXCLUDE_CONTACT_FROM_NOTIFICATIONS)
  const [, restoreContact] = useMutation(RESTORE_CONTACT_NOTIFICATIONS)

  const [showModalFor, setShowModalFor] = useState(null) // contact a excluir
  const [reason, setReason] = useState('')
  const [busyEmail, setBusyEmail] = useState(null)

  const contacts = data?.companyAccountingContacts || []

  const handleExclude = async () => {
    if (!showModalFor) return
    setBusyEmail(showModalFor.email)
    try {
      const res = await excludeContact({
        input: {
          companyId,
          email: showModalFor.email,
          name: showModalFor.name || undefined,
          type: NOTIFICATION_TYPE,
          reason: reason.trim() || undefined,
        },
      })
      if (res.error) {
        alert('Error al excluir el contacto: ' + res.error.message)
      } else {
        setShowModalFor(null)
        setReason('')
        refetch({ requestPolicy: 'network-only' })
      }
    } finally {
      setBusyEmail(null)
    }
  }

  const handleRestore = async (contact) => {
    if (!window.confirm(`¿Reactivar notificaciones de Factura Rechazada para ${contact.email}?`)) return
    setBusyEmail(contact.email)
    try {
      const res = await restoreContact({
        input: { companyId, email: contact.email, type: NOTIFICATION_TYPE },
      })
      if (res.error) {
        alert('Error al reactivar el contacto: ' + res.error.message)
      } else {
        refetch({ requestPolicy: 'network-only' })
      }
    } finally {
      setBusyEmail(null)
    }
  }

  const formatDateTime = (s) => {
    if (!s) return '-'
    return new Date(s).toLocaleString('es-CL', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <div className="content-card" style={{ marginBottom: '1.5rem' }}>
      <div className="card-header">
        <h3 className="card-title">Notificaciones — Contrapartes Contables</h3>
      </div>
      <div className="card-body">
        <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 0, marginBottom: '0.75rem' }}>
          Estos contactos reciben automáticamente el correo de <strong>Factura Rechazada</strong>.
          Puedes excluir a un contacto sin eliminarlo de la Configuración Inicial del cliente.
        </p>

        {fetching && <div className="loading-spinner"><div className="spinner"></div></div>}
        {error && <div className="empty-state"><p>Error al cargar contactos</p></div>}

        {!fetching && !error && contacts.length === 0 && (
          <div className="empty-state">
            <p>Esta empresa no tiene contrapartes contables configuradas.</p>
          </div>
        )}

        {!fetching && !error && contacts.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nombre</th>
                <th>Estado (Factura Rechazada)</th>
                <th style={{ textAlign: 'right' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => {
                const excluded = (c.optOutTypes || []).includes(NOTIFICATION_TYPE)
                const busy = busyEmail === c.email
                return (
                  <tr key={c.email}>
                    <td>{c.email}</td>
                    <td style={{ color: c.name ? 'inherit' : '#999' }}>{c.name || 'Sin nombre'}</td>
                    <td>
                      {excluded ? (
                        <div>
                          <span className="status-badge" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                            Excluido
                          </span>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            {c.optOutByEmail ? `Por ${c.optOutByEmail}` : ''}
                            {c.optOutAt ? ` • ${formatDateTime(c.optOutAt)}` : ''}
                          </div>
                          {c.optOutReason && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                              "{c.optOutReason}"
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="status-badge" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>
                          Recibe correos
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {excluded ? (
                        <button
                          className="action-btn secondary small"
                          onClick={() => handleRestore(c)}
                          disabled={busy}
                        >
                          {busy ? 'Reactivando...' : 'Reactivar'}
                        </button>
                      ) : (
                        <button
                          className="action-btn primary small"
                          onClick={() => { setShowModalFor(c); setReason('') }}
                          disabled={busy}
                          style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de confirmación */}
      {showModalFor && (
        <div className="modal-overlay" onClick={() => setShowModalFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Excluir de notificaciones</h3>
              <button className="modal-close" onClick={() => setShowModalFor(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                ¿Excluir a <strong>{showModalFor.email}</strong> del correo de Factura Rechazada?
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                El contacto seguirá visible en la sección "Contrapartes Contables" del cliente,
                pero dejará de recibir este correo automático. Puedes reactivarlo en cualquier momento.
              </p>
              <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                Razón (opcional)
              </label>
              <input
                type="text"
                className="form-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Ej: contacto solicitó no recibir más correos"
                style={{ width: '100%' }}
              />
            </div>
            <div className="modal-footer">
              <button
                className="action-btn secondary"
                onClick={() => setShowModalFor(null)}
                disabled={busyEmail === showModalFor.email}
              >
                Cancelar
              </button>
              <button
                className="action-btn primary"
                onClick={handleExclude}
                disabled={busyEmail === showModalFor.email}
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
              >
                {busyEmail === showModalFor.email ? 'Excluyendo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function CompanyDetail({ companyId, onBack, onSelectUser, currentUserEmail }) {
  const [showServicesModal, setShowServicesModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const [correlativeDraft, setCorrelativeDraft] = useState('')
  const [savingCorrelative, setSavingCorrelative] = useState(false)
  const [correlativeError, setCorrelativeError] = useState('')
  const [editingCorrelative, setEditingCorrelative] = useState(false)
  const [editingEntryDate, setEditingEntryDate] = useState(false)
  const [entryDateValue, setEntryDateValue] = useState('')
  const [savingEntryDate, setSavingEntryDate] = useState(false)
  
  const [result, reexecuteQuery] = useQuery({
    query: GET_COMPANY,
    variables: { id: companyId },
    requestPolicy: 'network-only',
  })

  const [, updateCompany] = useMutation(UPDATE_COMPANY)
  const [, updateCompanyStatus] = useMutation(UPDATE_COMPANY_STATUS)
  const [, updateHistoryComment] = useMutation(UPDATE_COMPANY_HISTORY_COMMENT)
  const [, deleteHistoryComment] = useMutation(DELETE_COMPANY_HISTORY_COMMENT)
  const [, updateCompanyPermissions] = useMutation(UPDATE_COMPANY_PERMISSIONS)
  const gqlClient = useClient()

  const { data, fetching, error } = result
  const company = data?.company

  useEffect(() => {
    setCorrelativeDraft(company?.correlative || '')
    setCorrelativeError('')
    setEditingCorrelative(false)
  }, [company?.correlative, companyId])

  const canEditCorrelative = /^MP\d+$/i.test(String(company?.correlative || '').trim())
    || !String(company?.correlative || '').trim()

  const fetchNextDefinitive = async () => {
    const result = await gqlClient
      .query(NEXT_COMPANY_CORRELATIVE, { kind: 'DEFINITIVE' }, { requestPolicy: 'network-only' })
      .toPromise()
    if (result.error) {
      throw new Error(result.error.message || 'No se pudo obtener el siguiente correlativo')
    }
    return result.data?.nextCompanyCorrelative || ''
  }

  const handleStartEditCorrelative = async () => {
    if (!canEditCorrelative) return
    setCorrelativeError('')
    setEditingCorrelative(true)
    try {
      const next = await fetchNextDefinitive()
      setCorrelativeDraft(next || '')
    } catch (e) {
      setCorrelativeDraft('')
      setCorrelativeError(e.message || 'No se pudo obtener el siguiente correlativo')
    }
  }

  const handleCancelEditCorrelative = () => {
    setEditingCorrelative(false)
    setCorrelativeError('')
    setCorrelativeDraft(company?.correlative || '')
  }

  const handleSaveCorrelative = async () => {
    if (!canEditCorrelative) return
    setSavingCorrelative(true)
    setCorrelativeError('')
    try {
      const result = await updateCompany({
        input: {
          companyId,
          correlative: correlativeDraft.trim(),
        },
      })
      if (result.error) {
        const message = result.error.message || 'No se pudo definir el correlativo'
        setCorrelativeError(message)
        if (/ya está asignado/i.test(message)) {
          try {
            const next = await fetchNextDefinitive()
            if (next) setCorrelativeDraft(next)
          } catch (_) {
            /* ignore */
          }
        }
        return
      }
      setEditingCorrelative(false)
      reexecuteQuery({ requestPolicy: 'network-only' })
    } finally {
      setSavingCorrelative(false)
    }
  }




  // Función para guardar comentarios de historial
  const handleSaveHistoryComment = async (historyType, changedAt, comment, index) => {
    try {
      const result = await updateHistoryComment({
        input: {
          companyId: companyId,
          historyType,
          changedAt: new Date(changedAt).toISOString(),
          comment: comment || '',
          index: index, // Enviar índice para identificación precisa
        },
      })
      
      if (result.error) {
        console.error('Error al guardar comentario:', result.error)
        alert('Error al guardar el comentario. Por favor, intenta de nuevo.')
        return
      }
      
      // Recargar en background sin bloquear UI
      setTimeout(() => {
        reexecuteQuery({ requestPolicy: 'network-only' })
      }, 0)
    } catch (error) {
      console.error('Error al guardar comentario:', error)
      alert('Error al guardar el comentario. Por favor, intenta de nuevo.')
    }
  }

  // Función para eliminar comentarios de historial
  const handleDeleteHistoryComment = async (historyType, changedAt, index) => {
    try {
      const result = await deleteHistoryComment({
        input: {
          companyId: companyId,
          historyType,
          changedAt: new Date(changedAt).toISOString(),
          index: index,
        },
      })
      
      if (result.error) {
        console.error('Error al eliminar comentario:', result.error)
        alert('Error al eliminar el comentario. Por favor, intenta de nuevo.')
        return
      }
      
      // Recargar en background sin bloquear UI
      setTimeout(() => {
        reexecuteQuery({ requestPolicy: 'network-only' })
      }, 0)
    } catch (error) {
      console.error('Error al eliminar comentario:', error)
      alert('Error al eliminar el comentario. Por favor, intenta de nuevo.')
    }
  }

  const handleUpdateService = async (newService) => {
    try {
      const result = await updateCompany({
        input: {
          companyId: companyId,
          scfo_plan: newService,
        },
      })
      
      if (result.error) {
        console.error('Error al actualizar servicio:', result.error)
        alert('Error al actualizar el servicio. Por favor, intenta de nuevo.')
        return
      }
      
      setShowServicesModal(false)
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      console.error('Error al actualizar servicio:', error)
      alert('Error al actualizar el servicio. Por favor, intenta de nuevo.')
    }
  }

  const toDateInputValue = (dateStr) => {
    if (!dateStr) return ''
    // Usar la parte de calendario del ISO (UTC) para no correr el día en Chile
    const raw = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString()
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/)
    return match ? match[1] : ''
  }

  const startEditingEntryDate = () => {
    setEntryDateValue(toDateInputValue(company?.entryDate))
    setEditingEntryDate(true)
  }

  const handleSaveEntryDate = async () => {
    setSavingEntryDate(true)
    try {
      const result = await updateCompany({
        input: {
          companyId: companyId,
          entryDate: entryDateValue || null,
        },
      })

      if (result.error) {
        console.error('Error al actualizar fecha de ingreso:', result.error)
        alert('Error al actualizar la fecha de ingreso. Por favor, intenta de nuevo.')
        return
      }

      setEditingEntryDate(false)
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      console.error('Error al actualizar fecha de ingreso:', error)
      alert('Error al actualizar la fecha de ingreso. Por favor, intenta de nuevo.')
    } finally {
      setSavingEntryDate(false)
    }
  }

  const handleUpdateStatus = async (newStatus, exitDate) => {
    try {
      // Usar updateCompanyStatus que registra el historial de estados
      const input = {
        companyId: companyId,
        newStatus: newStatus,
      }
      
      // Agregar exitDate solo si es BLOQUEADO y se proporcionó
      if (newStatus === 'BLOQUEADO' && exitDate) {
        input.exitDate = exitDate
      }
      
      const result = await updateCompanyStatus({ input })
      
      if (result.error) {
        console.error('Error al actualizar estado:', result.error)
        alert('Error al actualizar el estado. Por favor, intenta de nuevo.')
        return
      }
      
      setShowStatusModal(false)
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      console.error('Error al actualizar estado:', error)
      alert('Error al actualizar el estado. Por favor, intenta de nuevo.')
    }
  }

  // Handler para actualizar permisos de módulos
  const handleUpdatePermissions = async (permissions) => {
    try {
      const result = await updateCompanyPermissions({
        input: {
          companyId: companyId,
          modulePermissions: permissions.map(p => ({
            moduleId: p.moduleId,
            hasAccess: p.hasAccess,
            excludedFeatures: p.excludedFeatures || [],
          })),
        },
      })
      
      if (result.error) {
        console.error('Error al actualizar permisos:', result.error)
        alert('Error al actualizar los permisos. Por favor, intenta de nuevo.')
        return
      }
      
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      console.error('Error al actualizar permisos:', error)
      alert('Error al actualizar los permisos. Por favor, intenta de nuevo.')
    }
  }

  const formatCalendarDate = (dateStr, options = {}) => {
    if (!dateStr) return null
    // Fecha de solo día: tomar YYYY-MM-DD del ISO para no restar un día en UTC-3
    const raw = typeof dateStr === 'string' ? dateStr : new Date(dateStr).toISOString()
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return null
    const [, y, m, d] = match
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-CL', options)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return (
      formatCalendarDate(dateStr, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }) || '-'
    )
  }

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (fetching) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="content-card">
        <div className="card-body">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <p>Error al cargar la empresa</p>
            <button className="action-btn secondary" onClick={onBack}>
              Volver
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-section">
          <button className="back-btn" onClick={onBack}>
            ←
          </button>
          <div>
            <h1 className="page-title">{company.name}</h1>
            <p className="page-subtitle">{company.rut || 'Sin RUT'}</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="action-btn secondary small"
            onClick={() => setShowStatusModal(true)}
            title="Cambiar estado de la empresa"
          >
            Cambiar Estado
          </button>
        <StatusBadge status={company.adminStatus} />
        </div>
      </div>

      {/* Info General */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Información General</h3>
        </div>
        <div className="card-body">
          <div className="detail-info">
            <div className="info-item">
              <span className="info-label">Nombre</span>
              <span className="info-value">{company.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Alias</span>
              <span className="info-value">{company.alias || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">RUT</span>
              <span className="info-value">{company.rut || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Grupo empresarial</span>
              <span className="info-value">
                {company.group?.groupName || 'Sin grupo'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha de Ingreso</span>
              {editingEntryDate ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input
                    type="date"
                    value={entryDateValue}
                    onChange={(e) => setEntryDateValue(e.target.value)}
                    disabled={savingEntryDate}
                    style={{ padding: '0.35rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <button
                    className="action-btn primary small"
                    onClick={handleSaveEntryDate}
                    disabled={savingEntryDate}
                  >
                    {savingEntryDate ? 'Guardando...' : 'Guardar'}
                  </button>
                  <button
                    className="action-btn secondary small"
                    onClick={() => setEditingEntryDate(false)}
                    disabled={savingEntryDate}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="info-value">
                    {company.entryDate ? formatDate(company.entryDate) : 's/i'}
                  </span>
                  <button
                    className="action-btn secondary small"
                    onClick={startEditingEntryDate}
                    title="Editar fecha de ingreso"
                  >
                    Editar
                  </button>
                </div>
              )}
            </div>
            {/* Mostrar fecha de salida si está bloqueada */}
            {company.adminStatus === 'BLOQUEADO' && company.exitDate && (
              <div className="info-item">
                <span className="info-label" style={{ color: '#e74c3c' }}>Fecha de Salida</span>
                <span className="info-value" style={{ color: '#e74c3c', fontWeight: '600' }}>
                  {formatDate(company.exitDate)}
                </span>
              </div>
            )}
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
            <span className="info-label">Correlativo</span>
            {editingCorrelative && canEditCorrelative ? (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={correlativeDraft}
                    onChange={(e) => setCorrelativeDraft(e.target.value)}
                    placeholder="Siguiente disponible"
                    style={{
                      maxWidth: 100,
                      padding: '2px 8px',
                      fontSize: '1rem',
                      fontWeight: 400,
                      color: '#2c3e50',
                      lineHeight: 1.3,
                      height: 'auto',
                      minHeight: 0,
                    }}
                    aria-label="Correlativo definitivo"
                    autoFocus
                  />
                  <button
                    type="button"
                    className="action-btn primary small"
                    onClick={handleSaveCorrelative}
                    disabled={
                      savingCorrelative
                      || !correlativeDraft.trim()
                      || /^MP\d+$/i.test(correlativeDraft.trim())
                    }
                  >
                    {savingCorrelative ? 'Definiendo…' : 'Definir Correlativo'}
                  </button>
                  <button
                    type="button"
                    className="action-btn secondary small"
                    onClick={handleCancelEditCorrelative}
                    disabled={savingCorrelative}
                  >
                    Cancelar
                  </button>
                </div>
                {correlativeError && (
                  <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>{correlativeError}</div>
                )}
                <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                  Se sugiere el siguiente disponible. Puedes cambiarlo antes de confirmar.
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '0.35rem',
                  }}
                >
                  <span className="info-value" style={{ fontWeight: 600 }}>
                    {company.correlative || '—'}
                  </span>
                  {canEditCorrelative && (
                    <button
                      type="button"
                      onClick={handleStartEditCorrelative}
                      title="Definir correlativo definitivo"
                      aria-label="Editar correlativo"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: 4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#488e9d',
                        lineHeight: 1,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M12 20h9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {!canEditCorrelative && (
                  <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                    No se puede modificar.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Servicio SCFO */}
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="info-label">Servicio SCFO</span>
              <button 
                className="action-btn primary small"
                onClick={() => setShowServicesModal(true)}
              >
                Editar Servicio
              </button>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              {company.scfo_plan ? (
                <span className="role-badge user">
                  {AVAILABLE_SERVICES.find(s => s.value === company.scfo_plan)?.label || company.scfo_plan}
                </span>
              ) : (
                <span className="info-value">Sin servicio asignado</span>
              )}
            </div>
          </div>

          {/* Comentarios */}
          {company.comments && (
            <div style={{ marginTop: '1rem' }}>
              <span className="info-label">Comentarios</span>
              <p className="info-value" style={{ marginTop: '0.5rem' }}>
                {company.comments}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Usuarios de la empresa */}
      {(() => {
        const allUsers = company.users || [];
        const assignedUsers = allUsers.filter(u => u.accessType === 'ASIGNADO');

        const renderUserRow = (user, index) => {
          const roles = user.systemRoles || [];
          let roleLabel, roleClass;
          if (roles.includes('ADMIN')) { roleLabel = 'Admin'; roleClass = 'admin'; }
          else if (roles.includes('DPO')) { roleLabel = 'DPO'; roleClass = 'admin'; }
          else if (roles.includes('SUPER_COLABORADOR')) { roleLabel = 'Super Colaborador'; roleClass = 'colaborador'; }
          else if (roles.includes('COLABORADOR')) { roleLabel = 'Colaborador'; roleClass = 'colaborador'; }
          else { roleLabel = 'Usuario'; roleClass = 'user'; }

          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

          return (
            <tr
              key={user.userId || index}
              onClick={() => user.userId && onSelectUser && onSelectUser(user.userId)}
              style={{ cursor: user.userId && onSelectUser ? 'pointer' : 'default' }}
              className="clickable-row"
            >
              <td>{user.email || '-'}</td>
              <td style={{ color: fullName ? 'inherit' : '#999' }}>{fullName || 'Sin nombre'}</td>
              <td><span className={`role-badge ${roleClass}`}>{roleLabel}</span></td>
            </tr>
          );
        };

        const renderTable = (users) => (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nombre</th>
                <th>Rol</th>
              </tr>
            </thead>
            <tbody>{users.map(renderUserRow)}</tbody>
          </table>
        );

        return (
          <>
            <div className="content-card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3 className="card-title">Usuarios asignados a esta empresa ({assignedUsers.length})</h3>
              </div>
              <div className="card-body">
                {assignedUsers.length > 0 ? renderTable(assignedUsers) : (
                  <div className="empty-state">
                    <p>No hay usuarios asignados directamente</p>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* Notificaciones a Contrapartes Contables (opt-out por email) */}
      <AccountingNotificationsSection
        companyId={companyId}
        currentUserEmail={currentUserEmail}
      />

      {/* Permisos de Módulos */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Permisos de Módulos</h3>
          <button 
            className="action-btn primary small"
            onClick={() => setShowPermissionsModal(true)}
          >
            Configurar Permisos
          </button>
        </div>
        <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <div className="permissions-summary">
            {MODULE_CATEGORIES.map((cat) => {
              const modules = cat.subModules.length > 0 ? cat.subModules : [{ id: cat.id, name: cat.name }]
              return (
                <div key={cat.id} style={{ marginBottom: '0.5rem' }}>
                  {cat.subModules.length > 0 && (
                    <div style={{ fontWeight: 600, fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>{cat.name}</div>
                  )}
                  {modules.map((mod) => {
                    const perm = company.modulePermissions?.find(p => p.moduleId === mod.id)
                    const hasAccess = resolveModuleHasAccess(
                      perm,
                      mod.id,
                      true,
                      undefined,
                      company.modulePermissions,
                    )
                    return (
                      <div key={mod.id} className="permission-summary-item">
                        <span className="permission-module-name" style={!hasAccess ? { color: '#dc2626' } : undefined}>
                          {mod.name}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>


      {/* Historiales de Estados y Servicios */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
        {/* Historial de Estados */}
        {company.statusHistory?.length > 0 && (
          <div className="content-card">
            <div className="card-header">
              <h3 className="card-title">Historial de Estados</h3>
            </div>
            <div className="card-body" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <div className="history-timeline">
                {company.statusHistory
                  .slice()
                  .reverse()
                  .map((change, reversedIndex) => {
                    const originalIndex = company.statusHistory.length - 1 - reversedIndex
                    return (
                    <div key={originalIndex} className="history-item">
                      <div className="history-date">{formatDateTime(change.changedAt)}</div>
                      <div className="history-content">
                        <StatusBadge status={change.previousStatus} />
                        <span style={{ margin: '0 0.5rem' }}>→</span>
                        <StatusBadge status={change.newStatus} />
                      </div>
                      <div className="history-user">Por: {change.changedByEmail}</div>
                      <HistoryComment 
                        comment={change.comment}
                        commentAuthorEmail={change.commentAuthorEmail}
                        currentUserEmail={currentUserEmail}
                        onSave={(comment) => handleSaveHistoryComment('status', change.changedAt, comment, originalIndex)}
                        onDelete={() => handleDeleteHistoryComment('status', change.changedAt, originalIndex)}
                      />
                    </div>
                  )})}
              </div>
            </div>
          </div>
        )}

        {/* Historial de Servicios */}
        {company.serviceHistory?.length > 0 && (
          <div className="content-card">
            <div className="card-header">
              <h3 className="card-title">Historial de Servicios</h3>
            </div>
            <div className="card-body" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              <div className="history-timeline">
                {company.serviceHistory
                  .slice()
                  .reverse()
                  .map((change, reversedIndex) => {
                    const originalIndex = company.serviceHistory.length - 1 - reversedIndex
                    return (
                    <div key={originalIndex} className="history-item">
                      <div className="history-date">{formatDateTime(change.changedAt)}</div>
                      <div className="history-content">
                        <span className="status-badge" style={{ backgroundColor: '#e8f4f8', color: '#0d6efd' }}>
                          {change.previousService || 'Sin servicio'}
                        </span>
                        <span style={{ margin: '0 0.5rem' }}>→</span>
                        <span className="status-badge" style={{ backgroundColor: '#d1e7dd', color: '#198754' }}>
                          {change.newService || 'Sin servicio'}
                        </span>
                      </div>
                      <div className="history-user">Por: {change.changedByEmail}</div>
                      <HistoryComment 
                        comment={change.comment}
                        commentAuthorEmail={change.commentAuthorEmail}
                        currentUserEmail={currentUserEmail}
                        onSave={(comment) => handleSaveHistoryComment('service', change.changedAt, comment, originalIndex)}
                        onDelete={() => handleDeleteHistoryComment('service', change.changedAt, originalIndex)}
                      />
                    </div>
                  )
                  })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Edición de Servicio */}
      {showServicesModal && (
        <ServicesModal
          company={company}
          onClose={() => setShowServicesModal(false)}
          onConfirm={handleUpdateService}
        />
      )}

      {/* Modal de Cambio de Estado */}
      {showStatusModal && (
        <StatusModal
          company={company}
          onClose={() => setShowStatusModal(false)}
          onConfirm={handleUpdateStatus}
        />
      )}

      {/* Modal de Permisos de Módulos */}
      {showPermissionsModal && (
        <PermissionsModal
          company={company}
          isOpen={showPermissionsModal}
          onClose={() => setShowPermissionsModal(false)}
          onSave={handleUpdatePermissions}
        />
      )}
    </>
  )
}

export default CompanyDetail
